import React from 'react';
import tw from 'twin.macro';

/**
 * SYM-6199, shared since SYM-6353. The note under a preview that is showing
 * an older version than the customer's latest change, with the one thing
 * that helps: asking again.
 *
 * One note for both pop-ups (Steven, 27 Sep 2026): Track 1's Preview step
 * shows it when the server has not taken the latest edit; Bring to Life shows
 * it when a later refresh of its preview failed. It names no button and
 * blames nothing, because the same state is also reached while a rebuild is
 * simply still on its way.
 */
/* The note's history, from where it was first written (VideoWizard): */
/**
 * SYM-6199. Said under the preview while it predates the customer's edits.
 *
 * WHEN THIS CAN ACTUALLY BE SEEN, rewritten 22 Sep 2026 (Steven). Every way of
 * reaching this step asks for a rebuild and WAITS for it first
 * (goToStepRefreshingPreview), so on the ordinary path a customer cannot arrive
 * here looking at an old video. What is left is one case: the rebuild was asked
 * for and the server did not take it. The note is then the only thing telling
 * them the video on screen is not the one they just described.
 *
 * So it used to say "Press Next: Preview to build it again", and that was wrong
 * twice over: this step has no such button, and the thing it names is the thing
 * that just failed. It now offers the one action that can help, in the same
 * shape as the style examples' own Try again, so a customer meets one pattern
 * and not two.
 *
 * ⚠️ IT SAYS WHAT IS TRUE, NOT WHY. It said "the new one could not be built"
 * for half an hour, and the review gate showed that can be false: change a
 * headline to A, press Preview, and type B while that rebuild is still in
 * flight. A lands, the baseline becomes A, and the wizard still holds B -- so
 * the note appears although nothing failed and B was never sent. What IS always
 * true when this shows is the plain thing a customer can see: the video on
 * screen is not their latest change yet. Diagnosing the cause from this side of
 * the wire was a guess wearing a sentence's clothes.
 */
const Note = tw.p`flex-shrink-0 mt-3 max-w-sm text-center text-xs text-amber-600`;

const Retry = tw.button`ml-1 font-medium underline hover:no-underline cursor-pointer bg-transparent border-0 p-0 text-amber-700 disabled:opacity-60 disabled:cursor-default`;

export const StalePreviewNote: React.FC<{
  /** A rebuild is on its way: the button says so and waits. */
  busy: boolean;
  onRetry: () => void;
}> = ({ busy, onRetry }) => (
  <Note data-testid="stale-preview">
    This is not your latest change yet.
    <Retry type="button" disabled={busy} onClick={onRetry}>
      {busy ? 'Building…' : 'Try again'}
    </Retry>
  </Note>
);

export default StalePreviewNote;
